import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, DollarSign, Upload, Loader2, Plus, Trash2, FileText, RotateCcw, Pencil } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { Patient, PatientDocument } from '../types/patient';
import { Appointment, APPOINTMENT_TYPE_LABELS, AppointmentType } from '../types/appointment';
import { Invoice, InvoiceStatus, STATUS_COLORS as INV_STATUS_COLORS, STATUS_LABELS as INV_STATUS_LABELS } from '../types/invoice';
import { useAuth } from '../hooks/useAuth';
import { NewAppointmentModal } from '../components/NewAppointmentModal';
import { AppointmentDetailModal } from '../components/AppointmentDetailModal';
import { DeleteConfirmModal } from '../components/DeleteConfirmModal';

interface StatementEntry {
  date: string;
  type: 'invoice' | 'payment';
  description: string;
  invoiceId: string;
  debit: number;
  credit: number;
  runningBalance: number;
  timestamp: string;
}

type Tab = 'overview' | 'clinical' | 'appointments' | 'invoices' | 'documents';

const TABS: { id: Tab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'clinical', label: 'Clinical History' },
  { id: 'appointments', label: 'Appointments' },
  { id: 'invoices', label: 'Invoices' },
  { id: 'documents', label: 'Documents' },
];

export function PatientProfilePage() {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const { get, post, del, patch } = useApi();
  const { userRole } = useAuth();
  const [patient, setPatient] = useState<Patient | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Document state
  const [documents, setDocuments] = useState<PatientDocument[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Appointments and invoices state
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [statement, setStatement] = useState<StatementEntry[]>([]);

  // Deactivation modal state
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);

  // Edit modal state
  const [editSection, setEditSection] = useState<'personal' | 'contact' | 'medicalAid' | 'obstetric' | null>(null);
  const [editForm, setEditForm] = useState<Record<string, any>>({});
  const [editSaving, setEditSaving] = useState(false);

  // Appointment modals
  const [showNewApptModal, setShowNewApptModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);

  const fetchPatient = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const result = await get<Patient>(`/patients/${patientId}`);
      if (result.success && result.data) {
        setPatient(result.data);
      } else {
        setError(result.error || 'Patient not found');
      }
    } catch {
      setError('Failed to load patient');
    } finally {
      setLoading(false);
    }
  }, [patientId, get]);

  const fetchDocuments = useCallback(async () => {
    if (!patientId) return;
    try {
      const result = await get<PatientDocument[]>(`/patients/${patientId}/documents`);
      if (result.success && result.data) {
        setDocuments(result.data);
      }
    } catch {
      // Silently fail — documents tab will show empty
    }
  }, [patientId, get]);

  const fetchAppointments = useCallback(async () => {
    if (!patientId) return;
    try {
      // Use server-side patientId filter instead of fetching all appointments
      const result = await get<Appointment[]>(`/appointments?startDate=2020-01-01&endDate=2030-12-31&patientId=${patientId}`);
      if (result.success && result.data) {
        setAppointments(result.data);
      }
    } catch { /* silent */ }
  }, [patientId, get]);

  const fetchInvoices = useCallback(async () => {
    if (!patientId) return;
    try {
      const result = await get<Invoice[]>(`/invoices?patientId=${patientId}`);
      if (result.success && result.data) setInvoices(result.data);
    } catch { /* silent */ }
    try {
      const result = await get<StatementEntry[]>(`/invoices/patient/${patientId}/statement`);
      if (result.success && result.data) setStatement(result.data);
    } catch { /* silent */ }
  }, [patientId, get]);

  useEffect(() => {
    fetchPatient();
    fetchDocuments();
    fetchAppointments();
    fetchInvoices();
  }, [fetchPatient, fetchDocuments, fetchAppointments, fetchInvoices]);

  // --- Patient deactivation ---
  const handleDeactivate = useCallback(async () => {
    if (!patientId) return;
    const result = await del(`/patients/${patientId}`);
    if (result.success) {
      setShowDeactivateModal(false);
      navigate('/patients');
    } else {
      throw new Error(result.error || 'Failed to deactivate patient');
    }
  }, [patientId, del, navigate]);

  // --- Patient reactivation ---
  const handleReactivate = useCallback(async () => {
    if (!patientId) return;
    try {
      const result = await post(`/patients/${patientId}/reactivate`);
      if (result.success) {
        await fetchPatient();
      } else {
        setError(result.error || 'Failed to reactivate patient');
      }
    } catch {
      setError('Failed to reactivate patient');
    }
  }, [patientId, post, fetchPatient]);

  // --- Edit patient section ---
  const openEdit = useCallback((section: 'personal' | 'contact' | 'medicalAid' | 'obstetric') => {
    if (!patient) return;
    switch (section) {
      case 'personal':
        setEditForm({ title: patient.title, firstName: patient.firstName, lastName: patient.lastName, idNumber: patient.idNumber, dateOfBirth: patient.dateOfBirth, gender: patient.gender });
        break;
      case 'contact':
        setEditForm({ phone: patient.phone, email: patient.email, address: patient.address, city: patient.city, postalCode: patient.postalCode, nextOfKin: { ...patient.nextOfKin } });
        break;
      case 'medicalAid':
        setEditForm({ isMedicalAidPatient: patient.isMedicalAidPatient, medicalAid: { ...patient.medicalAid } });
        break;
      case 'obstetric':
        setEditForm({ obstetricHistory: { ...patient.obstetricHistory } });
        break;
    }
    setEditSection(section);
  }, [patient]);

  const handleEditSave = useCallback(async () => {
    if (!patientId || !editSection) return;
    setEditSaving(true);
    try {
      const result = await patch(`/patients/${patientId}`, editForm);
      if (result.success) {
        await fetchPatient();
        setEditSection(null);
      } else {
        setError(result.error || 'Failed to update patient');
      }
    } catch {
      setError('Failed to update patient');
    } finally {
      setEditSaving(false);
    }
  }, [patientId, editSection, editForm, patch, fetchPatient]);

  // --- Document upload ---
  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !patientId) return;

    setUploading(true);
    setError('');
    try {
      const result = await post<{ uploadUrl: string; gcsPath: string; documentId: string }>(
        `/patients/${patientId}/documents/upload-url`,
        {
          fileName: file.name,
          fileType: file.type || 'application/octet-stream',
          category: 'other',
        }
      );

      if (!result.success || !result.data) {
        setError(result.error || 'Failed to get upload URL');
        return;
      }

      const contentType = file.type || 'application/octet-stream';
      let gcsResponse: Response;
      try {
        gcsResponse = await fetch(result.data.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': contentType },
          body: file,
        });
      } catch {
        setError('Upload failed: network error.');
        return;
      }

      if (!gcsResponse.ok) {
        setError(`Upload failed (HTTP ${gcsResponse.status}).`);
        return;
      }

      const confirmResult = await post(
        `/patients/${patientId}/documents/${result.data.documentId}/confirm`
      );

      if (!confirmResult.success) {
        setError(confirmResult.error || 'Upload succeeded but failed to save record');
        return;
      }

      await fetchDocuments();
    } catch {
      setError('Failed to upload document.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [patientId, post, fetchDocuments]);

  // --- Document delete ---
  const handleDeleteDocument = useCallback(async (documentId: string, fileName: string) => {
    if (!patientId) return;
    if (!window.confirm(`Delete "${fileName}"? This will permanently remove the file.`)) return;

    try {
      const result = await del(`/patients/${patientId}/documents/${documentId}`);
      if (result.success) {
        setDocuments(prev => prev.filter(d => d.id !== documentId));
      } else {
        setError(result.error || 'Failed to delete document');
      }
    } catch {
      setError('Failed to delete document');
    }
  }, [patientId, del]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!patient && error) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={() => navigate('/patients')} className="text-blue-600 hover:underline">Back to patients</button>
        </div>
      </div>
    );
  }

  if (!patient) return null;

  return (
    <>
      {/* Deactivation confirmation modal — same password re-auth gate as clinical deletes */}
      {showDeactivateModal && (
        <DeleteConfirmModal
          title="Deactivate Patient"
          description={`This will deactivate ${patient.title} ${patient.firstName} ${patient.lastName}. The patient will be hidden from search results but can be reactivated at any time.`}
          onConfirm={handleDeactivate}
          onCancel={() => setShowDeactivateModal(false)}
        />
      )}

      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/patients')} className="text-gray-500 hover:text-gray-700">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  {patient.title} {patient.firstName} {patient.lastName}
                </h1>
                <p className="text-sm text-gray-500">ID: {patient.idNumber} | DOB: {patient.dateOfBirth}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {!patient.active && (
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">Inactive</span>
              )}
              {patient.isMedicalAidPatient && (
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                  {patient.medicalAid.scheme}
                </span>
              )}
              {patient.active ? (
                <button
                  onClick={() => setShowDeactivateModal(true)}
                  className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                  title="Deactivate patient"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              ) : (
                <button
                  onClick={handleReactivate}
                  className="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 transition-colors flex items-center gap-1"
                  title="Reactivate patient"
                >
                  <RotateCcw className="h-3.5 w-3.5" /> Reactivate
                </button>
              )}
            </div>
          </div>

          {error && (
            <div className="mt-3 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
          )}

          {/* Tabs */}
          <nav className="mt-4 -mb-px flex space-x-6 border-b border-gray-200">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Personal Details */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Personal Details</h3>
                <button onClick={() => openEdit('personal')} className="p-1.5 text-gray-400 hover:text-blue-600" title="Edit"><Pencil className="h-4 w-4" /></button>
              </div>
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Full Name</dt>
                  <dd className="text-sm text-gray-900">{patient.title} {patient.firstName} {patient.lastName}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">ID Number</dt>
                  <dd className="text-sm text-gray-900">{patient.idNumber}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Date of Birth</dt>
                  <dd className="text-sm text-gray-900">{patient.dateOfBirth}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Gender</dt>
                  <dd className="text-sm text-gray-900 capitalize">{patient.gender}</dd>
                </div>
              </dl>
            </div>

            {/* Contact */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Contact</h3>
                <button onClick={() => openEdit('contact')} className="p-1.5 text-gray-400 hover:text-blue-600" title="Edit"><Pencil className="h-4 w-4" /></button>
              </div>
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Phone</dt>
                  <dd className="text-sm text-gray-900">{patient.phone || 'Not provided'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Email</dt>
                  <dd className="text-sm text-gray-900">{patient.email || 'Not provided'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Address</dt>
                  <dd className="text-sm text-gray-900 text-right">{[patient.address, patient.city, patient.postalCode].filter(Boolean).join(', ') || 'Not provided'}</dd>
                </div>
              </dl>
              {patient.nextOfKin.name && (
                <>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mt-6 mb-3">Next of Kin</h4>
                  <dl className="space-y-2">
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-500">{patient.nextOfKin.relationship}</dt>
                      <dd className="text-sm text-gray-900">{patient.nextOfKin.name} — {patient.nextOfKin.phone}</dd>
                    </div>
                  </dl>
                </>
              )}
            </div>

            {/* Medical Aid */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Medical Aid</h3>
                <button onClick={() => openEdit('medicalAid')} className="p-1.5 text-gray-400 hover:text-blue-600" title="Edit"><Pencil className="h-4 w-4" /></button>
              </div>
              {patient.isMedicalAidPatient ? (
                <dl className="space-y-3">
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-500">Scheme</dt>
                    <dd className="text-sm text-gray-900">{patient.medicalAid.scheme}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-500">Plan</dt>
                    <dd className="text-sm text-gray-900">{patient.medicalAid.plan || 'Not provided'}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-500">Member Number</dt>
                    <dd className="text-sm text-gray-900">{patient.medicalAid.memberNumber}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-500">Main Member</dt>
                    <dd className="text-sm text-gray-900">{patient.medicalAid.mainMember || 'Not provided'}</dd>
                  </div>
                </dl>
              ) : (
                <p className="text-sm text-gray-500">Private patient (no medical aid)</p>
              )}
            </div>

            {/* Obstetric History */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Obstetric History</h3>
                <button onClick={() => openEdit('obstetric')} className="p-1.5 text-gray-400 hover:text-blue-600" title="Edit"><Pencil className="h-4 w-4" /></button>
              </div>
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">G/P/M/E</dt>
                  <dd className="text-sm text-gray-900">
                    G{patient.obstetricHistory.gravida} P{patient.obstetricHistory.para} M{patient.obstetricHistory.miscarriages} E{patient.obstetricHistory.ectopics}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">LMP</dt>
                  <dd className="text-sm text-gray-900">{patient.obstetricHistory.lmp || 'Not provided'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">EDD</dt>
                  <dd className="text-sm text-gray-900">{patient.obstetricHistory.edd || 'Not provided'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Blood Group</dt>
                  <dd className="text-sm">
                    {patient.obstetricHistory.bloodGroup
                      ? <span className="text-gray-900">{patient.obstetricHistory.bloodGroup}{patient.obstetricHistory.rhFactor === 'positive' ? '+' : patient.obstetricHistory.rhFactor === 'negative' ? '−' : ''}</span>
                      : <span className="text-amber-600">Not recorded</span>
                    }
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Rh Factor</dt>
                  <dd className="text-sm">
                    {patient.obstetricHistory.rhFactor
                      ? <span className="text-gray-900">{patient.obstetricHistory.rhFactor === 'positive' ? 'Positive' : 'Negative'}</span>
                      : <span className="text-amber-600">Not recorded</span>
                    }
                  </dd>
                </div>
                {patient.obstetricHistory.notes && (
                  <div>
                    <dt className="text-sm text-gray-500 mb-1">Notes</dt>
                    <dd className="text-sm text-gray-900">{patient.obstetricHistory.notes}</dd>
                  </div>
                )}
              </dl>
            </div>
          </div>
        )}

        {activeTab === 'clinical' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Clinical History</h3>
              <a
                href={`/clinical/${patientId}`}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 inline-flex items-center gap-2"
              >
                Open Clinical View
              </a>
            </div>
            <p className="text-sm text-gray-500">Open the clinical view to add SOAP notes, prescriptions, surgeries, and view full clinical history for this patient.</p>
          </div>
        )}

        {activeTab === 'appointments' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Appointments</h3>
              <button
                onClick={() => setShowNewApptModal(true)}
                className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center gap-1.5"
              >
                <Plus className="h-4 w-4" /> New Appointment
              </button>
            </div>
            {appointments.length === 0 ? (
              <p className="text-sm text-gray-500">No appointments found.</p>
            ) : (
              <div className="divide-y divide-gray-200">
                {appointments.map(a => (
                  <button key={a.id} onClick={() => setSelectedAppointment(a)} className="w-full py-3 flex items-center justify-between hover:bg-gray-50 text-left">
                    <div className="flex items-center gap-3">
                      <Calendar className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {a.date} at {a.startTime}–{a.endTime}
                        </p>
                        <p className="text-xs text-gray-500">
                          {APPOINTMENT_TYPE_LABELS[a.appointmentType as AppointmentType] || a.appointmentType}
                          {' · '}{a.status}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'invoices' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Invoices</h3>
              <button
                onClick={() => navigate(`/billing/new?patientId=${patientId}`)}
                className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center gap-1.5"
              >
                <Plus className="h-4 w-4" /> New Invoice
              </button>
            </div>
            {invoices.length === 0 ? (
              <p className="text-sm text-gray-500">No invoices found.</p>
            ) : (
              <div className="divide-y divide-gray-200">
                {invoices.map(inv => (
                  <button
                    key={inv.id}
                    onClick={() => navigate(`/billing/${inv.id}`)}
                    className="w-full py-3 flex items-center justify-between hover:bg-gray-50 text-left"
                  >
                    <div className="flex items-center gap-3">
                      <DollarSign className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{inv.invoiceNumber}</p>
                        <p className="text-xs text-gray-500">{inv.createdAt?.slice(0, 10)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-gray-900">R {inv.subtotal.toFixed(2)}</span>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${INV_STATUS_COLORS[inv.status as InvoiceStatus]}`}>
                        {INV_STATUS_LABELS[inv.status as InvoiceStatus]}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Statement / Ledger */}
            {statement.length > 0 && (
              <div className="mt-6 border-t border-gray-200 pt-6">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">Patient Statement</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-gray-500 text-xs uppercase border-b border-gray-200">
                        <th className="text-left pb-2 pr-4">Date</th>
                        <th className="text-left pb-2 pr-4">Description</th>
                        <th className="text-right pb-2 pr-4">Debit</th>
                        <th className="text-right pb-2 pr-4">Credit</th>
                        <th className="text-right pb-2">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statement.map((e, i) => (
                        <tr key={i} className="border-b border-gray-100">
                          <td className="py-2 pr-4 text-gray-600">{e.date}</td>
                          <td className="py-2 pr-4 text-gray-900">{e.description}</td>
                          <td className="py-2 pr-4 text-right text-gray-900">{e.debit > 0 ? `R ${e.debit.toFixed(2)}` : ''}</td>
                          <td className="py-2 pr-4 text-right text-green-700">{e.credit > 0 ? `R ${e.credit.toFixed(2)}` : ''}</td>
                          <td className={`py-2 text-right font-medium ${e.runningBalance > 0 ? 'text-red-600' : 'text-green-700'}`}>
                            R {e.runningBalance.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Patient Documents</h3>
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileUpload}
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  Upload Document
                </button>
              </div>
            </div>
            {documents.length === 0 ? (
              <p className="text-sm text-gray-500">No documents uploaded yet.</p>
            ) : (
              <ul className="divide-y divide-gray-200">
                {documents.map(doc => (
                  <li key={doc.id} className="py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-gray-400" />
                      <div>
                        <button
                          onClick={async () => {
                            const r = await get<{ downloadUrl: string }>(`/patients/${patientId}/documents/${doc.id}/download-url`);
                            if (r.success && r.data?.downloadUrl) {
                              window.open(r.data.downloadUrl, '_blank', 'noopener,noreferrer');
                            }
                          }}
                          className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline bg-transparent border-none cursor-pointer p-0"
                        >
                          {doc.fileName}
                        </button>
                        <p className="text-xs text-gray-500">
                          {doc.category} — uploaded {doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString() : ''}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteDocument(doc.id, doc.fileName)}
                      className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                      title="Delete document"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </main>

      {/* Edit Section Modal */}
      {editSection && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              Edit {editSection === 'personal' ? 'Personal Details' : editSection === 'contact' ? 'Contact' : editSection === 'medicalAid' ? 'Medical Aid' : 'Obstetric History'}
            </h2>
            <div className="space-y-3">
              {editSection === 'personal' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <select value={editForm.title || ''} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} className="w-full px-3 py-2 border rounded-md">
                      {['Mrs', 'Ms', 'Miss', 'Dr', 'Prof', 'Mr'].map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                      <input value={editForm.firstName || ''} onChange={e => setEditForm(f => ({ ...f, firstName: e.target.value }))} className="w-full px-3 py-2 border rounded-md" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                      <input value={editForm.lastName || ''} onChange={e => setEditForm(f => ({ ...f, lastName: e.target.value }))} className="w-full px-3 py-2 border rounded-md" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ID Number</label>
                    <input value={editForm.idNumber || ''} onChange={e => setEditForm(f => ({ ...f, idNumber: e.target.value }))} className="w-full px-3 py-2 border rounded-md" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                    <input type="date" value={editForm.dateOfBirth || ''} onChange={e => setEditForm(f => ({ ...f, dateOfBirth: e.target.value }))} className="w-full px-3 py-2 border rounded-md" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                    <select value={editForm.gender || ''} onChange={e => setEditForm(f => ({ ...f, gender: e.target.value }))} className="w-full px-3 py-2 border rounded-md">
                      <option value="female">Female</option>
                      <option value="male">Male</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </>
              )}
              {editSection === 'contact' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input value={editForm.phone || ''} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} className="w-full px-3 py-2 border rounded-md" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input value={editForm.email || ''} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} className="w-full px-3 py-2 border rounded-md" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <input value={editForm.address || ''} onChange={e => setEditForm(f => ({ ...f, address: e.target.value }))} className="w-full px-3 py-2 border rounded-md" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                      <input value={editForm.city || ''} onChange={e => setEditForm(f => ({ ...f, city: e.target.value }))} className="w-full px-3 py-2 border rounded-md" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
                      <input value={editForm.postalCode || ''} onChange={e => setEditForm(f => ({ ...f, postalCode: e.target.value }))} className="w-full px-3 py-2 border rounded-md" />
                    </div>
                  </div>
                  <h4 className="text-sm font-semibold text-gray-700 mt-4">Next of Kin</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input value={editForm.nextOfKin?.name || ''} onChange={e => setEditForm(f => ({ ...f, nextOfKin: { ...f.nextOfKin, name: e.target.value } }))} className="w-full px-3 py-2 border rounded-md" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                      <input value={editForm.nextOfKin?.relationship || ''} onChange={e => setEditForm(f => ({ ...f, nextOfKin: { ...f.nextOfKin, relationship: e.target.value } }))} className="w-full px-3 py-2 border rounded-md" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input value={editForm.nextOfKin?.phone || ''} onChange={e => setEditForm(f => ({ ...f, nextOfKin: { ...f.nextOfKin, phone: e.target.value } }))} className="w-full px-3 py-2 border rounded-md" />
                    </div>
                  </div>
                </>
              )}
              {editSection === 'medicalAid' && (
                <>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={editForm.isMedicalAidPatient || false} onChange={e => setEditForm(f => ({ ...f, isMedicalAidPatient: e.target.checked }))} className="rounded" />
                    Medical Aid Patient
                  </label>
                  {editForm.isMedicalAidPatient && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Scheme</label>
                        <input value={editForm.medicalAid?.scheme || ''} onChange={e => setEditForm(f => ({ ...f, medicalAid: { ...f.medicalAid, scheme: e.target.value } }))} className="w-full px-3 py-2 border rounded-md" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
                        <input value={editForm.medicalAid?.plan || ''} onChange={e => setEditForm(f => ({ ...f, medicalAid: { ...f.medicalAid, plan: e.target.value } }))} className="w-full px-3 py-2 border rounded-md" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Member Number</label>
                        <input value={editForm.medicalAid?.memberNumber || ''} onChange={e => setEditForm(f => ({ ...f, medicalAid: { ...f.medicalAid, memberNumber: e.target.value } }))} className="w-full px-3 py-2 border rounded-md" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Main Member</label>
                        <input value={editForm.medicalAid?.mainMember || ''} onChange={e => setEditForm(f => ({ ...f, medicalAid: { ...f.medicalAid, mainMember: e.target.value } }))} className="w-full px-3 py-2 border rounded-md" />
                      </div>
                    </>
                  )}
                </>
              )}
              {editSection === 'obstetric' && (
                <>
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Gravida</label>
                      <input type="number" min="0" value={editForm.obstetricHistory?.gravida ?? 0} onChange={e => setEditForm(f => ({ ...f, obstetricHistory: { ...f.obstetricHistory, gravida: parseInt(e.target.value) || 0 } }))} className="w-full px-3 py-2 border rounded-md" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Para</label>
                      <input type="number" min="0" value={editForm.obstetricHistory?.para ?? 0} onChange={e => setEditForm(f => ({ ...f, obstetricHistory: { ...f.obstetricHistory, para: parseInt(e.target.value) || 0 } }))} className="w-full px-3 py-2 border rounded-md" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Miscarriages</label>
                      <input type="number" min="0" value={editForm.obstetricHistory?.miscarriages ?? 0} onChange={e => setEditForm(f => ({ ...f, obstetricHistory: { ...f.obstetricHistory, miscarriages: parseInt(e.target.value) || 0 } }))} className="w-full px-3 py-2 border rounded-md" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Ectopics</label>
                      <input type="number" min="0" value={editForm.obstetricHistory?.ectopics ?? 0} onChange={e => setEditForm(f => ({ ...f, obstetricHistory: { ...f.obstetricHistory, ectopics: parseInt(e.target.value) || 0 } }))} className="w-full px-3 py-2 border rounded-md" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">LMP</label>
                      <input type="date" value={editForm.obstetricHistory?.lmp || ''} onChange={e => setEditForm(f => ({ ...f, obstetricHistory: { ...f.obstetricHistory, lmp: e.target.value } }))} className="w-full px-3 py-2 border rounded-md" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">EDD</label>
                      <input type="date" value={editForm.obstetricHistory?.edd || ''} onChange={e => setEditForm(f => ({ ...f, obstetricHistory: { ...f.obstetricHistory, edd: e.target.value } }))} className="w-full px-3 py-2 border rounded-md" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Blood Group</label>
                      <select
                        value={editForm.obstetricHistory?.bloodGroup || ''}
                        onChange={e => setEditForm(f => ({ ...f, obstetricHistory: { ...f.obstetricHistory, bloodGroup: e.target.value } }))}
                        className="w-full px-3 py-2 border rounded-md bg-white"
                      >
                        <option value="">— Select —</option>
                        <option value="A">A</option>
                        <option value="B">B</option>
                        <option value="AB">AB</option>
                        <option value="O">O</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Rh Factor</label>
                      <select
                        value={editForm.obstetricHistory?.rhFactor || ''}
                        onChange={e => setEditForm(f => ({ ...f, obstetricHistory: { ...f.obstetricHistory, rhFactor: e.target.value } }))}
                        className="w-full px-3 py-2 border rounded-md bg-white"
                      >
                        <option value="">— Select —</option>
                        <option value="positive">Positive</option>
                        <option value="negative">Negative</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea rows={2} value={editForm.obstetricHistory?.notes || ''} onChange={e => setEditForm(f => ({ ...f, obstetricHistory: { ...f.obstetricHistory, notes: e.target.value } }))} className="w-full px-3 py-2 border rounded-md" />
                  </div>
                </>
              )}
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => setEditSection(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
              <button onClick={handleEditSave} disabled={editSaving} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
                {editSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Appointment Modal — pre-populated with this patient */}
      {showNewApptModal && (
        <NewAppointmentModal
          defaultDate={new Date().toISOString().slice(0, 10)}
          defaultTime="09:00"
          initialPatient={patient}
          onClose={() => setShowNewApptModal(false)}
          onCreated={() => { setShowNewApptModal(false); fetchAppointments(); }}
        />
      )}

      {/* Appointment Detail Modal */}
      {selectedAppointment && (
        <AppointmentDetailModal
          appointment={selectedAppointment}
          onClose={() => setSelectedAppointment(null)}
          onUpdated={() => { setSelectedAppointment(null); fetchAppointments(); }}
        />
      )}
    </>
  );
}
